import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  TextField,
  Chip,
  Grid,
  Menu,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Comment as CommentIcon,
  Whatshot as HotIcon,
  Schedule as NewIcon,
  Star as TopIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  PushPin as PinIcon,
  FilterList as FilterIcon,
  AccessTime as TimeIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  FilterAlt as FilterAltIcon,
  Sort as SortIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Bookmark as BookmarkIcon
} from '@mui/icons-material';
import { CommunityBoardDAO } from '../../../repository/CommunityBoardDAO';
import { useUserContext } from '../../../context/user_context/UserContext';
import { useToast } from '../../../context/toast/ToastContext'
import DOMPurify from 'dompurify';
import { formatDistanceToNow } from 'date-fns';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { t } from 'i18next';
import AddPostModal from './modals/AddPostModal';
import { useNavigate } from 'react-router-dom';

const SORT_OPTIONS = {
  HOT: 'hot',
  NEW: 'new',
  TOP: 'top',
  RISING: 'rising'
};

const TIME_FILTERS = {
  DAY: 'today',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
  ALL: 'all'
};

const VIEW_FILTERS = {
  ALL: 'all',
  SAVED: 'saved',
  MY_POSTS: 'my_posts',
  MY_COMMENTS: 'my_comments'
};

const CATEGORIES = {
  General: {
    bg: '#E3F2FD',
    text: '#1565C0'
  },
  Question: {
    bg: '#F3E5F5',
    text: '#7B1FA2'
  },
  Discussion: {
    bg: '#E8F5E9',
    text: '#2E7D32'
  },
  Announcement: {
    bg: '#FFF3E0',
    text: '#E65100'
  },
  Event: {
    bg: '#E1F5FE',
    text: '#0277BD'
  },
  News: {
    bg: '#FFEBEE',
    text: '#C62828'
  },
  Help: {
    bg: '#E0F2F1',
    text: '#00695C'
  },
  Other: {
    bg: '#F3E5F5',
    text: '#6A1B9A'
  }
};

const BiziBoard = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.NEW);
  const [timeFilter, setTimeFilter] = useState(TIME_FILTERS.DAY);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [userVotes, setUserVotes] = useState({});
  const [showAddPost, setShowAddPost] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useMediaQuery('(max-width:600px)');
  const [anchorElCategory, setAnchorElCategory] = useState(null);
  const [anchorElTime, setAnchorElTime] = useState(null);
  const [anchorElSort, setAnchorElSort] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [viewFilter, setViewFilter] = useState(VIEW_FILTERS.ALL);
  const [userComments, setUserComments] = useState([]);
  const [commentsLastVisible, setCommentsLastVisible] = useState(null);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [anchorElViewFilter, setAnchorElViewFilter] = useState(null);
  
  const { firebaseUser } = useUserContext();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const handlePostClick = (postId) => {
    navigate(`/biziboard/${postId}`);
  };

  const fetchPosts = useCallback(async (isInitialFetch = false) => {
    if (loading || (!hasMore && !isInitialFetch)) {
      return;
    }
    
    setLoading(true);

    try {
      if (viewFilter === VIEW_FILTERS.MY_COMMENTS) {
        const { comments, lastVisible, hasMore } = await CommunityBoardDAO.fetchUserComments(
          firebaseUser.uid,
          {
            lastVisible: isInitialFetch ? null : commentsLastVisible,
            limit: 5
          }
        );

        setUserComments(prevComments => {
          if (isInitialFetch) return comments;
          const existingIds = new Set(prevComments.map(c => c.id));
          const uniqueNewComments = comments.filter(comment => !existingIds.has(comment.id));
          return [...prevComments, ...uniqueNewComments];
        });
        setCommentsLastVisible(lastVisible);
        setHasMoreComments(hasMore);
      } else {
        const { posts: newPosts, lastVisible: lastDoc, hasMore: hasMorePosts } = await CommunityBoardDAO.fetchPosts({
          sortBy,
          timeFilter,
          lastVisible: isInitialFetch ? null : lastVisible,
          queryLimit: 5,
          showSavedOnly: viewFilter === VIEW_FILTERS.SAVED,
          showUserPosts: viewFilter === VIEW_FILTERS.MY_POSTS,
          userId: firebaseUser?.uid
        });

        setPosts(prevPosts => {
          if (isInitialFetch) return newPosts;
          const existingIds = new Set(prevPosts.map(p => p.id));
          const uniqueNewPosts = newPosts.filter(post => !existingIds.has(post.id));
          return [...prevPosts, ...uniqueNewPosts];
        });
        setLastVisible(lastDoc);
        setHasMore(hasMorePosts);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      showToast('Failed to load content', 'error');
    } finally {
      setLoading(false);
    }
  }, [sortBy, timeFilter, lastVisible, loading, hasMore, viewFilter, firebaseUser, commentsLastVisible]);

  useEffect(() => {
    setPosts([]); // Clear existing posts
    setUserComments([]); // Clear existing comments
    setLastVisible(null); // Reset pagination
    setCommentsLastVisible(null);
    setHasMore(true); // Reset hasMore flags
    setHasMoreComments(true);
    fetchPosts(true); // Fetch first batch
  }, [sortBy, timeFilter, viewFilter]);

  const handleLoadMore = () => {
    if (!loading && ((viewFilter === VIEW_FILTERS.MY_COMMENTS && hasMoreComments) || hasMore)) {
      fetchPosts(false);
    }
  };

  // Update the loading indicator to show the Load More button
  const renderLoadingIndicator = () => {
    const isCommentsView = viewFilter === VIEW_FILTERS.MY_COMMENTS;
    const noMoreContent = isCommentsView ? !hasMoreComments : !hasMore;
    const hasContent = isCommentsView ? userComments.length > 0 : posts.length > 0;

    if (noMoreContent) {
      return hasContent && (
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: 'center',
            my: 4
          }}
        >
          You're all caught up!
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loading}
            sx={{ minWidth: 200 }}
          >
            Load More {isCommentsView ? 'Comments' : 'Posts'}
          </Button>
        )}
      </Box>
    );
  };

  // Update useEffect for user votes
  useEffect(() => {
    if (!firebaseUser) return;
    const unsubscribeVotes = CommunityBoardDAO.subscribeToUserVotes(firebaseUser.uid, (votesData) => { setUserVotes(votesData); });
    return () => unsubscribeVotes && unsubscribeVotes();
  }, [firebaseUser]);

  const handleVote = async (postId, _incrementValue, isUpvote) => {
    if (!firebaseUser) {
      showToast('You must be logged in to vote', 'error');
      return;
    }

    let voteChange = 0;
    try {
      const existingVote = userVotes?.[postId];
      
      // If clicking the same vote type (e.g. upvote → remove upvote)
      if (existingVote === isUpvote) {
        // If we're removing an upvote, we need to subtract 1
        // If we're removing a downvote, we need to add 1
        voteChange = existingVote ? -1 : 1;
      }
      // If changing vote type (e.g. upvote → downvote)
      else if (existingVote !== undefined && existingVote !== null) {
        // If switching from upvote to downvote: -2
        // If switching from downvote to upvote: +2
        voteChange = isUpvote ? 2 : -2;
      }
      // If no previous vote
      else {
        // New upvote: +1
        // New downvote: -1
        voteChange = isUpvote ? 1 : -1;
      }

      // Update posts state optimistically
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId 
            ? { ...post, vote_score: (post.vote_score || 0) + voteChange }
            : post
        )
      );

      // Make the API call
      await CommunityBoardDAO.voteOnPost(postId, firebaseUser.uid, voteChange, isUpvote);
    } catch (error) {
      // Revert optimistic update on error
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId 
            ? { ...post, vote_score: (post.vote_score || 0) - voteChange }
            : post
        )
      );
      console.error('Error voting:', error);
      showToast('Failed to vote', 'error');
    }
  };

  const filterPosts = useCallback((posts) => {
    // First filter the posts based on category and search term
    const filteredPosts = posts.filter(post => {
      const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
      const matchesSearch = !searchTerm || 
        post.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.content.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });

    // Then sort to ensure sticky posts are at the top
    return filteredPosts.sort((a, b) => {
      if (a.is_sticky && !b.is_sticky) return -1;
      if (!a.is_sticky && b.is_sticky) return 1;
      return 0;
    });
  }, [selectedCategory, searchTerm]);

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      <AppSidebar
        content={
          <Box 
            sx={{ 
              maxWidth: '100%',
              p: 0
            }}
          >
            <Grid container alignItems="center">
              <Grid item xs={12}>
                <Grid container alignItems="center" justifyContent="space-between">
                  {!isMobile && (
                    <>
                      <Grid item>
                        <Typography variant="h5">{t(StringKeys.BIZIBOARD)}</Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
                {isMobile && (
                  <Typography variant="h6" sx={{ mb: 2 }}>{t(StringKeys.BIZIBOARD)}</Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  px: { xs: 1, sm: 0 }
                }}>
                  {/* Desktop View */}
                  {!isMobile && (
                    <>
                      {firebaseUser && (
                        <ToggleButtonGroup
                          value={viewFilter}
                          exclusive
                          onChange={(e, newValue) => {
                            if (newValue !== null) {
                              setViewFilter(newValue);
                            }
                          }}
                          size="small"
                          sx={{ mr: 2 }}
                        >
                          <ToggleButton value={VIEW_FILTERS.ALL}>
                            All Posts
                          </ToggleButton>
                          <ToggleButton value={VIEW_FILTERS.SAVED}>
                            <BookmarkIcon sx={{ mr: 0.5 }} />
                            Saved
                          </ToggleButton>
                          <ToggleButton value={VIEW_FILTERS.MY_POSTS}>
                            <PersonIcon sx={{ mr: 0.5 }} />
                            My Posts
                          </ToggleButton>
                          <ToggleButton value={VIEW_FILTERS.MY_COMMENTS}>
                            <CommentIcon sx={{ mr: 0.5 }} />
                            My Comments
                          </ToggleButton>
                        </ToggleButtonGroup>
                      )}

                      {/* Only show these filters if not viewing comments */}
                      {viewFilter !== VIEW_FILTERS.MY_COMMENTS && (
                        <>
                          <TextField
                            placeholder={t(StringKeys.SEARCH_POSTS)}
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ 
                              minWidth: 200,
                              mr: 2,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5
                              }
                            }}
                          />
                          <Select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            size="small"
                            sx={{ minWidth: 120, mr: 2 }}
                          >
                            <MenuItem value="all">All Categories</MenuItem>
                            {Object.keys(CATEGORIES).map(category => (
                              <MenuItem key={category} value={category}>{category}</MenuItem>
                            ))}
                          </Select>
                          <Select
                            value={timeFilter}
                            onChange={(e) => setTimeFilter(e.target.value)}
                            size="small"
                            sx={{ minWidth: 100, mr: 2 }}
                          >
                            {Object.entries(TIME_FILTERS).map(([key, value]) => (
                              <MenuItem key={value} value={value}>
                                {key.charAt(0) + key.slice(1).toLowerCase()}
                              </MenuItem>
                            ))}
                          </Select>
                          <Button
                              variant="contained"
                              color="primary"
                              onClick={() => setShowAddPost(true)}
                              startIcon={<AddIcon />}
                            >
                              {t(StringKeys.CREATE_POST)}
                          </Button>
                          {/* <Tabs 
                            value={sortBy}
                            onChange={(e, newValue) => setSortBy(newValue)}
                            sx={{ ml: 'auto' }}
                          >
                            <Tab 
                              icon={<HotIcon />} 
                              label="Hot" 
                              value={SORT_OPTIONS.HOT}
                              sx={{ minWidth: 'auto' }}
                            />
                            <Tab 
                              icon={<NewIcon />} 
                              label="New" 
                              value={SORT_OPTIONS.NEW}
                              sx={{ minWidth: 'auto' }}
                            />
                            <Tab 
                              icon={<TopIcon />} 
                              label="Top" 
                              value={SORT_OPTIONS.TOP}
                              sx={{ minWidth: 'auto' }}
                            />
                            <Tab 
                              icon={<TrendingUpIcon />} 
                              label="Rising" 
                              value={SORT_OPTIONS.RISING}
                              sx={{ minWidth: 'auto' }}
                            />
                          </Tabs> */}
                        </>
                      )}
                    </>
                  )}

                  {/* Mobile View */}
                  {isMobile && !showSearch && (
                    <Box sx={{ 
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between'
                    }}>
                      <IconButton 
                        onClick={() => setShowSearch(true)}
                        size="small"
                      >
                        <SearchIcon sx={{ fontSize: '1.25rem' }} />
                      </IconButton>

                      {firebaseUser && (
                        <IconButton
                          size="small"
                          onClick={(e) => setAnchorElViewFilter(e.currentTarget)}
                        >
                          <PersonIcon sx={{ fontSize: '1.25rem' }} />
                        </IconButton>
                      )}

                      <IconButton
                        size="small"
                        onClick={(e) => setAnchorElCategory(e.currentTarget)}
                      >
                        <FilterIcon sx={{ fontSize: '1.25rem' }} />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={(e) => setAnchorElTime(e.currentTarget)}
                      >
                        <TimeIcon sx={{ fontSize: '1.25rem' }} />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => setShowAddPost(true)}
                        color="primary"
                      >
                        <AddIcon sx={{ fontSize: '1.25rem' }} />
                      </IconButton>
                    </Box>
                  )}

                  {/* Mobile Search View */}
                  {isMobile && showSearch && (
                    <Box sx={{ 
                      display: 'flex', 
                      flex: 1,
                      gap: 1,
                      alignItems: 'center'
                    }}>
                      <IconButton 
                        onClick={() => setShowSearch(false)}
                        size="small"
                      >
                        <ArrowBackIcon sx={{ fontSize: '1.25rem' }} />
                      </IconButton>
                      <TextField
                        autoFocus
                        placeholder={t(StringKeys.SEARCH_POSTS)}
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ 
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5
                          }
                        }}
                      />
                    </Box>
                  )}
                </Box>

                {/* Mobile View Filter Menu */}
                <Menu
                  anchorEl={anchorElViewFilter}
                  open={Boolean(anchorElViewFilter)}
                  onClose={() => setAnchorElViewFilter(null)}
                >
                  <MenuItem 
                    onClick={() => {
                      setViewFilter(VIEW_FILTERS.ALL);
                      setAnchorElViewFilter(null);
                    }}
                    selected={viewFilter === VIEW_FILTERS.ALL}
                  >
                    {t(StringKeys.ALL_POSTS)}
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      setViewFilter(VIEW_FILTERS.SAVED);
                      setAnchorElViewFilter(null);
                    }}
                    selected={viewFilter === VIEW_FILTERS.SAVED}
                  >
                    <BookmarkIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                    {t(StringKeys.SAVED)}
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      setViewFilter(VIEW_FILTERS.MY_POSTS);
                      setAnchorElViewFilter(null);
                    }}
                    selected={viewFilter === VIEW_FILTERS.MY_POSTS}
                  >
                    <PersonIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                    {t(StringKeys.MY_POSTS)}
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      setViewFilter(VIEW_FILTERS.MY_COMMENTS);
                      setAnchorElViewFilter(null);
                    }}
                    selected={viewFilter === VIEW_FILTERS.MY_COMMENTS}
                  >
                    <CommentIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                    My Comments
                  </MenuItem>
                </Menu>

                {/* Mobile Menus */}
                <Menu
                  anchorEl={anchorElCategory}
                  open={Boolean(anchorElCategory)}
                  onClose={() => setAnchorElCategory(null)}
                >
                  <MenuItem 
                    value="all"
                    onClick={() => {
                      setSelectedCategory('all');
                      setAnchorElCategory(null);
                    }}
                    selected={selectedCategory === 'all'}
                  >
                    All Categories
                  </MenuItem>
                  {Object.keys(CATEGORIES).map(category => (
                    <MenuItem 
                      key={category} 
                      onClick={() => {
                        setSelectedCategory(category);
                        setAnchorElCategory(null);
                      }}
                      selected={selectedCategory === category}
                    >
                      {category}
                    </MenuItem>
                  ))}
                </Menu>

                <Menu
                  anchorEl={anchorElTime}
                  open={Boolean(anchorElTime)}
                  onClose={() => setAnchorElTime(null)}
                >
                  {Object.entries(TIME_FILTERS).map(([key, value]) => (
                    <MenuItem 
                      key={value}
                      onClick={() => {
                        setTimeFilter(value);
                        setAnchorElTime(null);
                      }}
                      selected={timeFilter === value}
                    >
                      {key.charAt(0) + key.slice(1).toLowerCase()}
                    </MenuItem>
                  ))}
                </Menu>

                {/* <Menu
                  anchorEl={anchorElSort}
                  open={Boolean(anchorElSort)}
                  onClose={() => setAnchorElSort(null)}
                >
                  <MenuItem 
                    onClick={() => {
                      setSortBy(SORT_OPTIONS.HOT);
                      setAnchorElSort(null);
                    }}
                    selected={sortBy === SORT_OPTIONS.HOT}
                  >
                    <HotIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                    Hot
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      setSortBy(SORT_OPTIONS.NEW);
                      setAnchorElSort(null);
                    }}
                    selected={sortBy === SORT_OPTIONS.NEW}
                  >
                    <NewIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                    New
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      setSortBy(SORT_OPTIONS.TOP);
                      setAnchorElSort(null);
                    }}
                    selected={sortBy === SORT_OPTIONS.TOP}
                  >
                    <TopIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                    Top
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      setSortBy(SORT_OPTIONS.RISING);
                      setAnchorElSort(null);
                    }}
                    selected={sortBy === SORT_OPTIONS.RISING}
                  >
                    <TrendingUpIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                    Rising
                  </MenuItem>
                </Menu> */}

                {/* Posts list */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {viewFilter === VIEW_FILTERS.MY_COMMENTS ? (
                    userComments.map((comment) => (
                      <Paper
                        key={comment.id}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => handlePostClick(comment.postId)}
                      >
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Commented on: {comment.postTopic}
                        </Typography>
                        <Typography 
                          variant="body2"
                          dangerouslySetInnerHTML={{ 
                            __html: DOMPurify.sanitize(comment.message)
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {formatDistanceToNow(comment.created_at.toDate())} ago
                        </Typography>
                      </Paper>
                    ))
                  ) : (
                    filterPosts(posts).map((post) => (
                      <Paper
                        key={post.id} 
                        elevation={0}
                        sx={{ 
                          '&:hover': { bgcolor: 'action.hover' },
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          position: 'relative',
                          overflow: 'hidden',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          if (e.target.closest('.vote-buttons, .pin-button')) return;
                          handlePostClick(post.id);
                        }}
                      >
                        {post.is_sticky && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: 4,
                              bgcolor: 'primary.main'
                            }}
                          />
                        )}
                        
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 2,
                          p: { xs: 1.5, sm: 2 }
                        }}>
                          <Box 
                            className="vote-buttons"
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              minWidth: 40
                            }}
                          >
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleVote(post.id, 1, true);
                              }}
                              color={userVotes[post.id] === true ? 'primary' : 'default'}
                              size="small"
                            >
                              <ArrowUpIcon />
                            </IconButton>
                            <Typography variant="body2">{post.vote_score || 0}</Typography>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleVote(post.id, -1, false);
                              }}
                              color={userVotes[post.id] === false ? 'error' : 'default'}
                              size="small"
                            >
                              <ArrowDownIcon />
                            </IconButton>
                          </Box>
                          
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              gap: 1, 
                              alignItems: 'center', 
                              flexWrap: { xs: 'wrap', sm: 'nowrap' }
                            }}>
                              {!isMobile && post.category && (
                                <Chip
                                  label={post.category}
                                  size="small"
                                  sx={{ 
                                    borderRadius: 1,
                                    backgroundColor: CATEGORIES[post.category]?.bg || CATEGORIES['Other'].bg,
                                    color: CATEGORIES[post.category]?.text || CATEGORIES['Other'].text,
                                    border: 'none',
                                    fontWeight: 500
                                  }}
                                />
                              )}
                              <Typography 
                                variant="h6" 
                                component="h2"
                                sx={{ 
                                  fontSize: { xs: '1rem', sm: '1.1rem' },
                                  fontWeight: 'medium',
                                  wordBreak: 'break-word',
                                  flex: 1
                                }}
                              >
                                {post.topic}
                                {post.is_sticky && (
                                  <PinIcon 
                                    sx={{ 
                                      ml: 1, 
                                      fontSize: '0.9em',
                                      verticalAlign: 'middle',
                                      color: 'primary.main'
                                    }} 
                                  />
                                )}
                              </Typography>
                            </Box>
                            
                            {isMobile && post.category && (
                              <Box sx={{ mt: 0.5, mb: 1 }}>
                                <Chip
                                  label={post.category}
                                  size="small"
                                  sx={{ 
                                    borderRadius: 1,
                                    backgroundColor: CATEGORIES[post.category]?.bg || CATEGORIES['Other'].bg,
                                    color: CATEGORIES[post.category]?.text || CATEGORIES['Other'].text,
                                    border: 'none',
                                    fontWeight: 500
                                  }}
                                />
                              </Box>
                            )}
                            
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ mb: 1 }}
                            >
                              {t(StringKeys.POSTED_BY)} {post.creator} • {formatDistanceToNow(post.created_at.toDate())} {t(StringKeys.AGO)}
                            </Typography>
                            
                            <Typography 
                              variant="body2" 
                              component="div"
                              sx={{ 
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                                color: 'text.secondary'
                              }}
                              dangerouslySetInnerHTML={{ 
                                __html: DOMPurify.sanitize(post.content.substring(0, 300)) 
                              }}
                            />
                            
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              mt: 1,
                              flexWrap: 'wrap'
                            }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                color: 'text.secondary'
                              }}>
                                <CommentIcon sx={{ fontSize: 'small', mr: 0.5 }} />
                                <Typography variant="body2">
                                  {post.comment_count || 0} {t(StringKeys.COMMENTS)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    ))
                  )}

                  {/* Replace the Load More button section with the infinite scroll indicator */}
                  {renderLoadingIndicator()}
                </Box>

                {/* Add Post Modal */}
                <AddPostModal
                  show={showAddPost}
                  onHide={() => setShowAddPost(false)}
                  onPostCreated={() => {
                    setPosts([]);
                    setLastVisible(null);
                    setHasMore(true);
                    fetchPosts(true);
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        }
      />
    </div>
  );
};

export default BiziBoard;